import React from 'react'
import { Box, Avatar, Typography, Stack, Chip, LinearProgress } from '@mui/material'
import { Verified, LocationOn } from '@mui/icons-material'
import { getPhysicianProfileImage } from 'src/pages/Physicians/helpers'

/**
 * Calculate profile completion percentage
 */
const calculateProfileCompletion = (profile, profileType) => {
  if (!profile) return 0

  let completion = 0

  if (profileType === 'candidate') {
    // Candidate weights (total: 100%)
    if (profile.profileImage) completion += 10
    if (profile.city && profile.state && profile.phone) completion += 15
    if (profile.specialties?.length > 0 && profile.yearsExperience) completion += 20
    if (profile.resume) completion += 20
    if (profile.preferredStates?.length > 0) completion += 20
    if (profile.about) completion += 15
  } else if (profileType === 'employer') {
    // Employer weights (total: 100%)
    if (profile.profileImage) completion += 20
    if (profile.city && profile.state && profile.phone) completion += 30
    if (profile.jobTitle) completion += 25
    if (profile.about) completion += 25
  }

  return Math.min(completion, 100)
}

/**
 * Get progress color based on completion percentage
 */
const getProgressColor = (percentage) => {
  if (percentage >= 80) return 'success.main'
  if (percentage >= 50) return 'primary.main'
  return 'warning.main'
}

/**
 * ProfileHeader - Compact centered profile header with completion indicator
 * Features:
 * - Profile photo
 * - Name with verification badge
 * - Headline/title
 * - Location
 * - Profile completion indicator
 */
const ProfileHeader = ({ profile, profileType = 'candidate' }) => {
  const completion = calculateProfileCompletion(profile, profileType)
  const progressColor = getProgressColor(completion)

  // Use the same helper function as FileSection to get the profile image URL
  const profileImageUrl = getPhysicianProfileImage(profile.profileImage)

  // Get headline based on profile type
  const getHeadline = () => {
    if (profileType === 'candidate') {
      const specialty = profile.specialties?.[0]?.name || 'Healthcare Professional'
      const experience = profile.yearsExperience
        ? `${profile.yearsExperience} Years Experience`
        : 'Healthcare Professional'
      return `${specialty} | ${experience}`
    } else if (profileType === 'employer') {
      return profile.jobTitle || 'Recruiter'
    }
    return ''
  }

  // Determine search status
  const getSearchStatus = () => {
    if (profileType !== 'candidate') return null
    if (profile.searchStatus === 'actively_searching') {
      return { label: 'Actively Searching', color: 'success' }
    } else if (profile.searchStatus === 'open_to_opportunities') {
      return { label: 'Open to Opportunities', color: 'info' }
    }
    return null
  }

  const searchStatus = getSearchStatus()

  return (
    <Box
      sx={{
        width: '100%',
        py: 2,
        textAlign: 'center',
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Profile Photo */}
      <Avatar
        src={profileImageUrl}
        sx={{
          width: 80,
          height: 80,
          mx: 'auto',
          mb: 1.5,
          border: '3px solid',
          borderColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      />

      {/* Name and Verification */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
      >
        <Typography variant="h5" fontWeight={700}>
          {profile.firstName} {profile.lastName}
        </Typography>
        {profile.emailVerifiedAt && (
          <Verified sx={{ color: 'primary.main', fontSize: 22 }} />
        )}
      </Stack>

      {/* Headline */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 400 }}>
        {getHeadline()}
      </Typography>

      {/* Location and Status */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="center"
        flexWrap="wrap"
        gap={1}
        sx={{ mt: 1.5, mb: 2 }}
      >
        {profile.city && profile.state && (
          <Chip
            icon={<LocationOn />}
            label={`${profile.city}, ${profile.state}`}
            variant="outlined"
            size="small"
          />
        )}
        {searchStatus && (
          <Chip
            label={searchStatus.label}
            color={searchStatus.color}
            size="small"
            sx={{
              fontWeight: 600,
              '&::before': {
                content: '""',
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: `${searchStatus.color}.main`,
                display: 'inline-block',
                mr: 0.5,
                animation: 'pulse 2s infinite',
              },
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
        )}
      </Stack>

      {/* Profile Completion Progress Bar */}
      <Box sx={{ maxWidth: 400, mx: 'auto', px: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Profile Strength
          </Typography>
          <Typography variant="caption" fontWeight={600} color={progressColor}>
            {completion}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={completion}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: progressColor,
            },
          }}
        />
      </Box>
    </Box>
  )
}

export default ProfileHeader
